// Человеческая подпись фазы первичного синка — единая для баннера под топбаром
// и для скелетона дашборда. Живёт отдельно от `usePowerSyncStatus`, чтобы
// потребитель, которому нужен и статус, и подпись, не подписывался на статус
// дважды.

import type { TFunction } from "i18next";
import type { PowerSyncStatusView } from "./use-powersync-status.js";

/**
 * Идёт ли ПЕРВИЧНЫЙ синк — то есть стоит ли рассказывать пользователю о
 * загрузке библиотеки. `initializing` включён наравне с активными фазами:
 * между чекпоинтами `downloading` гаснет, а до первого `connect()` статус
 * молчит вовсе, и раньше в эти интервалы баннер исчезал.
 */
export function isFirstSyncInProgress(
	view: Pick<PowerSyncStatusView, "phase" | "hasSynced">,
): boolean {
	if (view.hasSynced) return false;
	return (
		view.phase === "initializing" || view.phase === "connecting" || view.phase === "downloading"
	);
}

/**
 * @returns Строка «что происходит» или `null`, если первичный синк уже прошёл
 *          (тогда рассказывать нечего).
 */
export function syncStatusLabel(
	t: TFunction<"common">,
	view: Pick<
		PowerSyncStatusView,
		"phase" | "hasSynced" | "downloadProgress" | "pendingUploadCount"
	>,
): string | null {
	if (view.hasSynced) return null;

	if (view.downloadProgress) {
		return t("offline.syncDownloadingPercent", {
			percent: view.downloadProgress.percent,
			downloaded: view.downloadProgress.downloaded,
			total: view.downloadProgress.total,
		});
	}
	if (view.pendingUploadCount > 0) {
		return t("offline.syncDownloadingPending", { count: view.pendingUploadCount });
	}
	switch (view.phase) {
		case "downloading":
			return t("offline.syncDownloading");
		case "connecting":
			return t("offline.syncConnecting");
		case "initializing":
			return t("offline.syncInitializing");
		default:
			return null;
	}
}

/**
 * Подпись для гейта загрузки страницы: почему экран всё ещё скелетон.
 *
 * Отличие от {@link syncStatusLabel} — покрывает случай «синк давно прошёл, а
 * данных на экране ещё нет» (reopen: `hasSynced` уже true с прошлого запуска,
 * но ждём auth.me и первые запросы). Там про библиотеку говорить нельзя, она
 * готова, поэтому подпись нейтральная.
 *
 * @returns `null`, когда объяснять НЕ надо: офлайн и ошибку синка описывают
 *          свои баннеры, а «Подготовка библиотеки…» поверх «Нет соединения»
 *          противоречила бы им.
 */
export function pageWaitLabel(
	t: TFunction<"common">,
	view: Pick<
		PowerSyncStatusView,
		"phase" | "hasSynced" | "downloadProgress" | "pendingUploadCount"
	>,
): string | null {
	if (view.phase === "offline" || view.phase === "error") return null;
	return syncStatusLabel(t, view) ?? t("state.loadingApp");
}
